// Sample data for the matchmaking platform

export interface Teacher {
  id: string;        // teacher user ID (used for inquiry/subscription flow)
  profileId?: string; // teacher profile UUID (used for public profile page)
  handle?: string | null; // public URL handle (e.g. "john-smith-math")
  name: string;
  avatar: string;
  bio: string;
  subjects: string[];
  grades: string[];
  languages: string[];
  experience: number;
  hourlyRate: number;
  teachingStyle: string;
  rating: number;
  reviewCount: number;
  verified: boolean;
  online: boolean;
  offline: boolean;
  hybrid: boolean;
  location: string;
  demoVideoUrl?: string;
  qualifications: string[];
  teacherProfileId?: string;
  availability: string;
  responseTime: string;
  sponsored?: boolean;
}

export interface Student {
  id: string;
  name: string;
  avatar: string;
  grade: string;
  subjects: string[];
  goals: string;
  budget: { min: number; max: number };
  preferredStyle: string;
  preferredExperience: string;
  preferredGender?: string;
  online: boolean;
  offline: boolean;
  location: string;
}

export interface Review {
  id: string;
  teacherId: string;
  studentName: string;
  studentAvatar: string;
  rating: number;
  comment: string;
  date: string;
  subject: string;
}

export interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar: string;
  content: string;
  timestamp: string;
  read: boolean;
}

export interface MatchResult {
  teacher: Teacher;
  compatibilityScore: number;
  breakdown: {
    subjectMatch: number;
    styleMatch: number;
    budgetFit: number;
    locationMatch: number;
    experienceMatch: number;
  };
}

export const subjects = [
  "Mathematics", "Physics", "Chemistry", "Biology", "English",
  "Spanish", "French", "German", "Mandarin", "Japanese",
  "Computer Science", "History", "Geography", "Economics",
  "Music", "Art", "Physical Education", "Psychology"
];

export const subjectTopics: Record<string, string[]> = {
  "Mathematics": ["Algebra", "Geometry", "Calculus", "Trigonometry", "Statistics", "Linear Algebra", "Number Theory", "Discrete Math"],
  "Physics": ["Mechanics", "Thermodynamics", "Electromagnetism", "Optics", "Quantum Physics", "Astrophysics", "Nuclear Physics"],
  "Chemistry": ["Organic Chemistry", "Inorganic Chemistry", "Physical Chemistry", "Biochemistry", "Analytical Chemistry", "Polymer Chemistry"],
  "Biology": ["Cell Biology", "Genetics", "Ecology", "Anatomy", "Microbiology", "Evolutionary Biology", "Botany", "Zoology"],
  "English": ["Grammar", "Creative Writing", "Literature Analysis", "Essay Writing", "Poetry", "Public Speaking", "SAT/ACT Prep"],
  "Spanish": ["Conversational", "Grammar", "Reading Comprehension", "Business Spanish", "DELE Prep"],
  "French": ["Conversational", "Grammar", "Reading Comprehension", "Business French", "DELF Prep"],
  "German": ["Conversational", "Grammar", "Reading Comprehension", "Business German"],
  "Mandarin": ["Conversational", "Characters & Writing", "HSK Prep", "Business Mandarin"],
  "Japanese": ["Conversational", "Hiragana & Katakana", "Kanji", "JLPT Prep", "Business Japanese"],
  "Computer Science": ["Programming Basics", "Data Structures", "Algorithms", "Web Development", "Machine Learning", "Databases", "Cybersecurity"],
  "History": ["World History", "US History", "European History", "Ancient Civilizations", "Modern History", "AP History"],
  "Geography": ["Physical Geography", "Human Geography", "Cartography", "GIS", "Climate Studies"],
  "Economics": ["Microeconomics", "Macroeconomics", "Econometrics", "International Economics", "AP Economics"],
  "Music": ["Music Theory", "Piano", "Guitar", "Vocals", "Composition", "Music Production"],
  "Art": ["Drawing", "Painting", "Digital Art", "Art History", "Sculpture", "Graphic Design"],
  "Physical Education": ["Fitness Training", "Sports Coaching", "Nutrition", "Yoga", "Dance"],
  "Psychology": ["Developmental Psychology", "Cognitive Psychology", "Social Psychology", "Abnormal Psychology", "Research Methods"],
};

export const grades = [
  "Elementary (K-5)", "Middle School (6-8)", "High School (9-12)",
  "College", "Graduate", "Adult Learner"
];

export const teachingStyles = [
  "Interactive & Hands-on", "Lecture-based", "Discussion-focused",
  "Project-based", "Socratic Method", "Visual Learning",
  "Game-based", "One-on-one Teaching"
];

export const sampleTeachers: Teacher[] = [
  {
    id: "t1",
    name: "Dr. Sarah Mitchell",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400",
    bio: "Passionate mathematics educator with 12 years of experience. I believe every student can excel in math with the right guidance and patience. My approach combines visual learning with real-world applications.",
    subjects: ["Mathematics", "Physics"],
    grades: ["High School (9-12)", "College"],
    languages: ["English", "Spanish"],
    experience: 12,
    hourlyRate: 65,
    teachingStyle: "Interactive & Hands-on",
    rating: 4.9,
    reviewCount: 156,
    verified: true,
    online: true,
    offline: true,
    hybrid: true,
    location: "New York, NY",
    qualifications: ["Ph.D. Mathematics - MIT", "Teaching Certification - NY", "10+ years experience"],
    availability: "Weekdays 3PM-8PM, Weekends flexible",
    responseTime: "Within 2 hours",
    sponsored: true
  },
  {
    id: "t2",
    name: "Michael Chen",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    bio: "Former software engineer at Google, now dedicated to teaching the next generation of programmers. I make coding fun and accessible for all ages.",
    subjects: ["Computer Science", "Mathematics"],
    grades: ["Middle School (6-8)", "High School (9-12)", "College"],
    languages: ["English", "Mandarin"],
    experience: 8,
    hourlyRate: 75,
    teachingStyle: "Project-based",
    rating: 4.8,
    reviewCount: 98,
    verified: true,
    online: true,
    offline: false,
    hybrid: false,
    location: "San Francisco, CA",
    qualifications: ["M.S. Computer Science - Stanford", "Google Software Engineer (5 years)", "AWS Certified"],
    availability: "Weekdays 5PM-9PM",
    responseTime: "Within 1 hour",
    sponsored: true
  },
  {
    id: "t3",
    name: "Emma Rodriguez",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400",
    bio: "Native Spanish speaker and certified language instructor. I create immersive learning experiences that make language learning natural and enjoyable.",
    subjects: ["Spanish", "English"],
    grades: ["Elementary (K-5)", "Middle School (6-8)", "High School (9-12)", "Adult Learner"],
    languages: ["Spanish", "English", "Portuguese"],
    experience: 10,
    hourlyRate: 45,
    teachingStyle: "Interactive & Hands-on",
    rating: 5.0,
    reviewCount: 203,
    verified: true,
    online: true,
    offline: true,
    hybrid: true,
    location: "Miami, FL",
    qualifications: ["DELE C2 Certification", "TESOL Certified", "M.A. Applied Linguistics"],
    availability: "Flexible schedule",
    responseTime: "Within 30 minutes"
  },
  {
    id: "t4",
    name: "James Thompson",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400",
    bio: "Chemistry professor with a knack for breaking down complex concepts. My students consistently improve their grades and develop a genuine interest in science.",
    subjects: ["Chemistry", "Biology"],
    grades: ["High School (9-12)", "College"],
    languages: ["English"],
    experience: 15,
    hourlyRate: 70,
    teachingStyle: "Visual Learning",
    rating: 4.7,
    reviewCount: 89,
    verified: true,
    online: true,
    offline: true,
    hybrid: true,
    location: "Boston, MA",
    qualifications: ["Ph.D. Chemistry - Harvard", "Published Researcher", "15+ years teaching"],
    availability: "Weekends and evenings",
    responseTime: "Within 4 hours"
  },
  {
    id: "t5",
    name: "Lisa Park",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400",
    bio: "Music teacher specializing in piano and music theory. I've helped students from beginners to advanced levels achieve their musical dreams.",
    subjects: ["Music"],
    grades: ["Elementary (K-5)", "Middle School (6-8)", "High School (9-12)", "Adult Learner"],
    languages: ["English", "Korean"],
    experience: 7,
    hourlyRate: 55,
    teachingStyle: "One-on-one Teaching",
    rating: 4.9,
    reviewCount: 134,
    verified: true,
    online: true,
    offline: true,
    hybrid: false,
    location: "Los Angeles, CA",
    qualifications: ["M.A. Music Performance - Juilliard", "Royal Conservatory Certified", "Concert Pianist"],
    availability: "Weekdays 2PM-7PM",
    responseTime: "Within 2 hours"
  },
  {
    id: "t6",
    name: "David Williams",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400",
    bio: "History and economics teacher who brings the past to life. I connect historical events to modern-day issues to make learning relevant and engaging.",
    subjects: ["History", "Economics"],
    grades: ["High School (9-12)", "College"],
    languages: ["English", "French"],
    experience: 11,
    hourlyRate: 50,
    teachingStyle: "Discussion-focused",
    rating: 4.6,
    reviewCount: 67,
    verified: true,
    online: true,
    offline: false,
    hybrid: false,
    location: "Chicago, IL",
    qualifications: ["Ph.D. History - Yale", "Published Author", "Former University Professor"],
    availability: "Flexible",
    responseTime: "Within 3 hours"
  }
];

export const sampleReviews: Review[] = [
  {
    id: "r1",
    teacherId: "t1",
    studentName: "Alex Johnson",
    studentAvatar: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=100",
    rating: 5,
    comment: "Dr. Mitchell is amazing! She helped me go from struggling with calculus to getting an A. Her patience and teaching style made all the difference.",
    date: "2024-01-15",
    subject: "Mathematics"
  },
  {
    id: "r2",
    teacherId: "t1",
    studentName: "Maria Garcia",
    studentAvatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100",
    rating: 5,
    comment: "Best math teacher I've ever had! She explains concepts in ways that actually make sense.",
    date: "2024-01-10",
    subject: "Physics"
  },
  {
    id: "r3",
    teacherId: "t2",
    studentName: "Jason Lee",
    studentAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    rating: 5,
    comment: "Michael's real-world experience at Google makes his lessons invaluable. I learned more in a month than in a semester of school.",
    date: "2024-01-08",
    subject: "Computer Science"
  },
  {
    id: "r4",
    teacherId: "t3",
    studentName: "Sophie Turner",
    studentAvatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100",
    rating: 5,
    comment: "Emma's immersive approach to Spanish is fantastic! I feel so much more confident speaking now.",
    date: "2024-01-12",
    subject: "Spanish"
  }
];

export const sampleMessages: Message[] = [
  {
    id: "m1",
    senderId: "t1",
    senderName: "Dr. Sarah Mitchell",
    senderAvatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100",
    content: "Hi! Thank you for reaching out. I'd love to help you with calculus. What specific topics are you struggling with?",
    timestamp: "2024-01-20T14:30:00",
    read: true
  },
  {
    id: "m2",
    senderId: "t2",
    senderName: "Michael Chen",
    senderAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    content: "I saw your inquiry about Python programming. I have some availability this week if you'd like to schedule a trial lesson!",
    timestamp: "2024-01-20T10:15:00",
    read: false
  },
  {
    id: "m3",
    senderId: "t3",
    senderName: "Emma Rodriguez",
    senderAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    content: "¡Hola! I received your message about conversational Spanish. Let's get started! When are you available for our first session?",
    timestamp: "2024-01-19T16:45:00",
    read: true
  }
];

export function calculateCompatibility(teacher: Teacher, studentPreferences: {
  subjects: string[];
  budget: { min: number; max: number };
  preferredStyle?: string;
  online?: boolean;
  offline?: boolean;
  location?: string;
  experienceLevel?: string;
}): MatchResult {
  const subjectMatch = teacher.subjects.some(s => studentPreferences.subjects.includes(s)) ? 100 : 0;
  
  const budgetFit = teacher.hourlyRate >= studentPreferences.budget.min && 
                    teacher.hourlyRate <= studentPreferences.budget.max ? 100 : 
                    teacher.hourlyRate <= studentPreferences.budget.max * 1.2 ? 70 : 30;
  
  const styleMatch = studentPreferences.preferredStyle === teacher.teachingStyle ? 100 : 60;
  
  let locationMatch = 50;
  if (studentPreferences.online && teacher.online) locationMatch = 100;
  else if (studentPreferences.offline && teacher.offline && 
           studentPreferences.location === teacher.location) locationMatch = 100;
  
  let experienceMatch = 70;
  if (studentPreferences.experienceLevel === "Expert" && teacher.experience >= 10) experienceMatch = 100;
  else if (studentPreferences.experienceLevel === "Intermediate" && teacher.experience >= 5) experienceMatch = 100;
  else if (studentPreferences.experienceLevel === "Any") experienceMatch = 100;
  
  const overallScore = Math.round(
    (subjectMatch * 0.35) + 
    (budgetFit * 0.25) + 
    (styleMatch * 0.15) + 
    (locationMatch * 0.15) + 
    (experienceMatch * 0.10)
  );
  
  return {
    teacher,
    compatibilityScore: overallScore,
    breakdown: {
      subjectMatch,
      styleMatch,
      budgetFit,
      locationMatch,
      experienceMatch
    }
  };
}

export const studentTestimonials = [
  {
    id: 1,
    name: "Jennifer Adams",
    role: "Parent",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100",
    content: "This platform helped us find the perfect math teacher for our daughter. Her grades improved dramatically within just two months!",
    rating: 5
  },
  {
    id: 2,
    name: "Marcus Johnson",
    role: "College Student",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    content: "The matching algorithm is incredible. It connected me with a CS teacher who perfectly understood my learning style and goals.",
    rating: 5
  },
  {
    id: 3,
    name: "Sophia Lee",
    role: "High School Student",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    content: "Found an amazing Spanish teacher who made learning fun. I went from failing to fluent in less than a year!",
    rating: 5
  }
];

export const teacherTestimonials = [
  {
    id: 1,
    name: "Dr. Emily Chen",
    role: "Mathematics Teacher",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100",
    content: "BookMyCoaching has transformed my teaching career. I now have a steady stream of motivated students who truly value my expertise.",
    rating: 5
  },
  {
    id: 2,
    name: "Michael Torres",
    role: "Language Instructor",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100",
    content: "The platform handles all the matching so I can focus on what I love—teaching. My income has doubled since joining.",
    rating: 5
  },
  {
    id: 3,
    name: "Amanda Brooks",
    role: "Science Teacher",
    avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100",
    content: "The verification badge builds instant trust with parents. I've been able to raise my rates and still get more students.",
    rating: 5
  }
];

// Offerings types and data
export type OfferingType = "ad-hoc" | "group-class" | "course" | "workshop" | "tutoring";

export interface Offering {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherAvatar: string;
  title: string;
  description: string;
  type: OfferingType;
  subject: string;
  price: number;
  priceUnit: "per session" | "per month" | "total";
  duration: string;
  mode: "online" | "in-person" | "hybrid";
  rating: number;
  reviewCount: number;
  enrolled: number;
  maxStudents: number;
  startDate?: string;
  schedule?: string;
  image: string;
  sponsored?: boolean;
}

export const offeringTypes: { value: OfferingType; label: string }[] = [
  { value: "ad-hoc", label: "Ad-hoc Class" },
  { value: "group-class", label: "Group Class" },
  { value: "course", label: "Course" },
  { value: "workshop", label: "Workshop" },
  { value: "tutoring", label: "Tutoring" },
];

export const sampleOfferings: Offering[] = [
  {
    id: "o1",
    teacherId: "t1",
    teacherName: "Dr. Sarah Mitchell",
    teacherAvatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400",
    title: "AP Calculus Crash Course",
    description: "Intensive 8-week course covering all AP Calculus AB topics with practice exams and personalized feedback.",
    type: "course",
    subject: "Mathematics",
    price: 450,
    priceUnit: "total",
    duration: "8 weeks",
    mode: "online",
    rating: 4.9,
    reviewCount: 42,
    enrolled: 18,
    maxStudents: 25,
    startDate: "2026-03-01",
    schedule: "Tue & Thu, 6-7:30 PM",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=600",
    sponsored: true,
  },
  {
    id: "o2",
    teacherId: "t2",
    teacherName: "Michael Chen",
    teacherAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    title: "Python for Beginners Workshop",
    description: "A hands-on weekend workshop to learn Python fundamentals through fun projects and real-world examples.",
    type: "workshop",
    subject: "Computer Science",
    price: 120,
    priceUnit: "total",
    duration: "2 days",
    mode: "online",
    rating: 4.8,
    reviewCount: 31,
    enrolled: 22,
    maxStudents: 30,
    startDate: "2026-02-22",
    schedule: "Sat & Sun, 10 AM - 2 PM",
    image: "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=600",
  },
  {
    id: "o3",
    teacherId: "t3",
    teacherName: "Emma Rodriguez",
    teacherAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400",
    title: "Conversational Spanish Group",
    description: "Weekly group sessions focused on building conversational confidence through role-play and discussion.",
    type: "group-class",
    subject: "Spanish",
    price: 35,
    priceUnit: "per session",
    duration: "Ongoing",
    mode: "online",
    rating: 5.0,
    reviewCount: 56,
    enrolled: 8,
    maxStudents: 10,
    schedule: "Wed, 7-8 PM",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600",
  },
  {
    id: "o4",
    teacherId: "t4",
    teacherName: "James Thompson",
    teacherAvatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400",
    title: "Chemistry Lab Prep - Ad Hoc",
    description: "Book one-off sessions to get help with specific chemistry lab reports, experiments, or exam topics.",
    type: "ad-hoc",
    subject: "Chemistry",
    price: 60,
    priceUnit: "per session",
    duration: "1 hour",
    mode: "hybrid",
    rating: 4.7,
    reviewCount: 19,
    enrolled: 0,
    maxStudents: 1,
    image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=600",
  },
  {
    id: "o5",
    teacherId: "t5",
    teacherName: "Lisa Park",
    teacherAvatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400",
    title: "Piano Fundamentals Course",
    description: "12-week structured course for absolute beginners to learn piano basics, reading sheet music, and playing simple songs.",
    type: "course",
    subject: "Music",
    price: 600,
    priceUnit: "total",
    duration: "12 weeks",
    mode: "in-person",
    rating: 4.9,
    reviewCount: 38,
    enrolled: 5,
    maxStudents: 8,
    startDate: "2026-03-10",
    schedule: "Mon & Wed, 4-5 PM",
    image: "https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=600",
    sponsored: true,
  },
  {
    id: "o6",
    teacherId: "t6",
    teacherName: "David Williams",
    teacherAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400",
    title: "SAT History Tutoring",
    description: "Personalized 1-on-1 tutoring sessions tailored to your SAT US History preparation needs.",
    type: "tutoring",
    subject: "History",
    price: 55,
    priceUnit: "per session",
    duration: "1 hour",
    mode: "online",
    rating: 4.6,
    reviewCount: 24,
    enrolled: 3,
    maxStudents: 5,
    schedule: "Flexible",
    image: "https://images.unsplash.com/photo-1447069387593-a5de0862481e?w=600",
  },
  {
    id: "o7",
    teacherId: "t1",
    teacherName: "Dr. Sarah Mitchell",
    teacherAvatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400",
    title: "Physics Problem-Solving Workshop",
    description: "Weekend workshop focused on mastering physics problem-solving techniques for competitive exams.",
    type: "workshop",
    subject: "Physics",
    price: 95,
    priceUnit: "total",
    duration: "1 day",
    mode: "hybrid",
    rating: 4.8,
    reviewCount: 15,
    enrolled: 12,
    maxStudents: 20,
    startDate: "2026-03-15",
    schedule: "Sat, 9 AM - 4 PM",
    image: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?w=600",
  },
  {
    id: "o8",
    teacherId: "t2",
    teacherName: "Michael Chen",
    teacherAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    title: "Web Development Group Class",
    description: "Learn HTML, CSS, and JavaScript in a collaborative group setting with weekly projects.",
    type: "group-class",
    subject: "Computer Science",
    price: 40,
    priceUnit: "per session",
    duration: "Ongoing",
    mode: "online",
    rating: 4.9,
    reviewCount: 27,
    enrolled: 14,
    maxStudents: 15,
    schedule: "Sat, 11 AM - 1 PM",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600",
  },
];

// Legacy export for backwards compatibility
export const testimonials = studentTestimonials;
